import type React from "react";
import type {ReactElement} from "react";
import {observer} from "mobx-react-lite";
import {Box, LinearProgress, type LinearProgressProps, Skeleton, Stack, Typography} from "@mui/material";
import FactoryIcon from "~/icons/svg/FactoryIcon.svg?react";
import FainancialIcon from '~/icons/svg/DollarCoinIcon.svg?react'
import VerifiedIcon from '~/icons/svg/VerifiedIcon.svg?react'
import NetworkIcon from '~/icons/svg/NetworkIcon.svg?react'
import LockIcon from '~/icons/svg/LockIcon.svg?react'
import ESGIcon from '~/icons/svg/ESGIcon.svg?react'
import {COLORS} from "~/style/colors-ts";
import {MONO_FONT} from "~/style/fonts";
import {SourceLogo} from "~/components/source-logo/SourceLogo";
import {useSourceLogoAllowed} from "~/pages/supplier-360/hooks/useSourceLogoAllowed";
import {useSupplierHealthScoresQuery} from "~/pages/supplier-360/tabs/SupplierInsights/hooks/useSupplierHealthScoresQuery";
import {useSupplierKpiSummaryQuery} from "~/pages/supplier-360/hooks/useSupplierKpiSummaryQuery";
import type {HealthScoreDomain, SupplierHealthScore} from "~/pages/supplier-360/types/Supplier360Types";
import {
    compositeHealthScore,
    formatHealthDelta,
    HEALTH_BAND_LABEL,
    HEALTH_TIER_COLOR,
    healthTier,
    sortHealthDomains,
} from "~/pages/supplier-360/tabs/SupplierInsights/supplierHealth";


/**
 * The Insights tab's "Supplier health — Semantic" view (§3.8): our own six-domain score over
 * `risk_signal`, not the third-party D&B one. The design's overall "±N pts vs last quarter" line
 * is left out — the API only carries a delta per domain, and averaging those isn't the same figure.
 */
export const SemanticHealthBlock: React.FC<{entityId: number}> = observer(({entityId}) => {
    const isLogoAllowed = useSourceLogoAllowed('insights');
    const {data: rows, isLoading, isError} = useSupplierHealthScoresQuery(entityId);
    const {data: kpiSummary} = useSupplierKpiSummaryQuery(entityId);

    const domains = sortHealthDomains(rows ?? []);
    // Prefer the server's figure so this always matches the header's "Risk" pill
    const composite = kpiSummary && kpiSummary.risk_score > 0 ? kpiSummary.risk_score : compositeHealthScore(domains);
    const tier = healthTier(composite);

    return (
        <>
            <Typography variant="h6" fontWeight={600} fontSize={15} sx={{mb: 2}}>Supplier health</Typography>
            {isLogoAllowed('semanticVision') && <SourceLogo source="semanticVision" sx={{mb: 1.5}}/>}
            {isLoading && <Skeleton variant="rectangular" height={260}/>}

            {isError && (
                <Typography variant="body2" color="text.secondary">Health scores could not be loaded.</Typography>
            )}

            {!isLoading && !isError && domains.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    Health scores have not been computed for this supplier yet.
                </Typography>
            )}

            {domains.length > 0 && (
                <Stack direction={{xs: 'column', sm: 'row'}} gap={2}>
                    <Stack
                        justifyContent="center"
                        gap={0.75}
                        sx={{width: {xs: 'auto', sm: 300}, flexShrink: 0, p: 2.5, borderRadius: 1.5, bgcolor: HEALTH_TIER_COLOR[tier].tint}}
                    >
                        <Typography sx={{fontFamily: MONO_FONT, fontSize: 40, fontWeight: 600, lineHeight: 1, letterSpacing: '-0.03em', color: HEALTH_TIER_COLOR[tier].ink}}>
                            {composite}
                        </Typography>
                        <Typography variant="caption" fontWeight={600} sx={{color: COLORS.grey[900]}}>
                            {HEALTH_BAND_LABEL[tier]}
                        </Typography>
                    </Stack>
                    <Stack justifyContent="center" sx={{flex: 1, minWidth: 0}}>
                        {domains.map(row => <DomainRow key={row.id} row={row}/>)}
                    </Stack>
                </Stack>
            )}
        </>
    );
});

function deltaColors(delta: number): {color: string, bgcolor: string} {
    const rounded = Math.round(delta);
    if (rounded > 0) return {color: COLORS.success.main, bgcolor: COLORS.success[100]};
    if (rounded < 0) return {color: COLORS.error.main, bgcolor: COLORS.error[100]};
    return {color: COLORS.grey[600], bgcolor: COLORS.grey[50]};
}

/**
 * Presentation only — the design assigns these client-side per domain name (§4.11). `Partial`, not
 * `Record`: this block's own query is pinned to our own source (`supplierHealthScoreUrl()`), which
 * never writes the `'Overall'` domain (that's another source's flat figure, docs §2.11) — so there's
 * deliberately no icon for it here.
 */
const DOMAIN_ICON: Partial<Record<HealthScoreDomain, ReactElement>> = {
    Financial: <FainancialIcon width={16} height={16}/>,
    Operational: <FactoryIcon width={16} height={16}/>,
    Compliance: <VerifiedIcon width={16} height={16}/>,
    Cybersecurity: <LockIcon width={16} height={16}/>,
    ESG: <ESGIcon width={16} height={16}/>,
    Regional: <NetworkIcon width={16} height={16}/>,
};

function DomainRow({row}: {row: SupplierHealthScore}) {
    const tier = healthTier(row.score);
    const score = Math.round(row.score);

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '22px minmax(0, 190px) 1fr 30px 38px',
                alignItems: 'center',
                gap: 1.25,
                py: 1,
                borderTop: 1,
                borderColor: COLORS.grey[150],
                '&:first-of-type': {borderTop: 'none'},
            }}
        >
            <Box sx={{display: 'inline-flex', color: COLORS.grey[600], fontSize: 16}}>{DOMAIN_ICON[row.domain]}</Box>
            <Stack direction="row" alignItems="center" gap={1} sx={{minWidth: 0}}>
                <Typography variant="body2" noWrap>{row.domain}</Typography>
                {row.is_weakest && (
                    <Box
                        component="span"
                        sx={{
                            fontSize: 11.5,
                            fontWeight: 500,
                            px: 0.75,
                            borderRadius: 1,
                            color: HEALTH_TIER_COLOR[tier].ink,
                            bgcolor: HEALTH_TIER_COLOR[tier].tint,
                        }}
                    >
                        weakest
                    </Box>
                )}
            </Stack>
            <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, row.score))}
                aria-label={`${row.domain} health`}
                color={HEALTH_TIER_COLOR[tier].name as LinearProgressProps['color']}
            />
            <Typography variant="caption" sx={{fontFamily: MONO_FONT, textAlign: 'right'}}>
                {score}
            </Typography>
            <Box
                component="span"
                title="vs prior period"
                sx={{
                    fontFamily: MONO_FONT,
                    fontSize: 11.5,
                    textAlign: 'center',
                    px: 0.75,
                    borderRadius: 0.5,
                    ...deltaColors(row.delta_vs_prior_period),
                }}
            >
                {formatHealthDelta(row.delta_vs_prior_period)}
            </Box>
        </Box>
    );
}